import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import IntroSplash from "@/components/site/IntroSplash";
import { VideoPopupProvider } from "@/components/site/VideoPopup";

import Home from "@/pages/Home";
import Artists from "@/pages/Artists";
import ArtistDetail from "@/pages/ArtistDetail";
import Videos from "@/pages/Videos";
import About from "@/pages/About";
import Distribution from "@/pages/Distribution";
import { Login, Register } from "@/pages/Auth";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import ContractSign from "@/pages/ContractSign";
import DeliverableView from "@/pages/DeliverableView";
import EventRsvp from "@/pages/EventRsvp";
import DeckPublicViewer from "@/pages/DeckPublicViewer";
import CSNLanding from "@/pages/CSNLanding";
import MusicCatalog from "@/pages/MusicCatalog";
import SingleRelease from "@/pages/SingleRelease";

import AdminLayout from "@/components/admin/AdminLayout";
import AdminOverview from "@/pages/admin/Overview";
import Contracts from "@/pages/admin/Contracts";
import Services from "@/pages/admin/Services";
import Payments from "@/pages/admin/Payments";
import Inquiries from "@/pages/admin/Inquiries";
import ArtistsAdmin from "@/pages/admin/ArtistsAdmin";
import VideosAdmin from "@/pages/admin/VideosAdmin";
import TeamAdmin from "@/pages/admin/TeamAdmin";
import SocialScheduler from "@/pages/admin/SocialScheduler";
import EmailAdmin from "@/pages/admin/EmailAdmin";
import Deliverables from "@/pages/admin/Deliverables";
import CalendarAdmin from "@/pages/admin/CalendarAdmin";
import PitchDeckAdmin from "@/pages/admin/PitchDeckAdmin";
import ContactsAdmin from "@/pages/admin/ContactsAdmin";
import AgentAdmin from "@/pages/admin/AgentAdmin";

function PublicLayout() {
  return (
    <>
      <Header />
      <Outlet />
      <Footer />
    </>
  );
}

function MinimalLayout() {
  return (
    <>
      <Header />
      <Outlet />
    </>
  );
}

function AnimatedRoutes() {
  const loc = useLocation();
  // CSN page has its own video splash — skip the global brand splash there
  const showIntro = !["/csn", "/music", "/big-mad", "/fit-in"].some((p) => loc.pathname.startsWith(p));
  return (
    <>
      {showIntro && <IntroSplash />}
      <AnimatePresence mode="wait">
      <Routes location={loc} key={loc.pathname}>
        {/* Public site (with footer) */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/artists" element={<Artists />} />
          <Route path="/artists/:slug" element={<ArtistDetail />} />
          <Route path="/videos" element={<Videos />} />
          <Route path="/about" element={<About />} />
          <Route path="/distribution" element={<Distribution />} />
          {/* Pretty artist URLs (e.g. /keezy). MUST be the last public route so
              all other named routes resolve first. */}
          <Route path="/:slug" element={<ArtistDetail />} />
        </Route>

        {/* Auth + special pages (header only, no footer to keep flow clean) */}
        <Route element={<MinimalLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/sign/:token" element={<ContractSign />} />
          <Route path="/deliverables/:slug" element={<DeliverableView />} />
          <Route path="/event/:id" element={<EventRsvp />} />
        </Route>

        {/* Standalone deck viewer — no site chrome, just the deck */}
        <Route path="/deck/:token" element={<DeckPublicViewer />} />

        {/* Hidden release landing — no site chrome */}
        <Route path="/csn" element={<CSNLanding />} />
        <Route path="/music" element={<MusicCatalog />} />
        <Route path="/big-mad" element={<SingleRelease slug="big-mad" />} />
        <Route path="/fit-in" element={<SingleRelease slug="fit-in" />} />

        {/* Admin */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminOverview />} />
          <Route path="contracts" element={<Contracts />} />
          <Route path="services" element={<Services />} />
          <Route path="payments" element={<Payments />} />
          <Route path="inquiries" element={<Inquiries />} />
          <Route path="artists" element={<ArtistsAdmin />} />
          <Route path="videos" element={<VideosAdmin />} />
          <Route path="team" element={<TeamAdmin />} />
          <Route path="social" element={<SocialScheduler />} />
          <Route path="email" element={<EmailAdmin />} />
          <Route path="deliverables" element={<Deliverables />} />
          <Route path="calendar" element={<CalendarAdmin />} />
          <Route path="pitch-deck" element={<PitchDeckAdmin />} />
          <Route path="contacts" element={<ContactsAdmin />} />
          <Route path="agent" element={<AgentAdmin />} />
        </Route>
      </Routes>
    </AnimatePresence>
    </>
  );
}

function App() {
  return (
    <div className="App grain">
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#0c0c0e", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" } }} />
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <VideoPopupProvider>
              <AnimatedRoutes />
            </VideoPopupProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
